import React, { useState, useEffect } from "react";
import { 
  Settings2, ChevronDown, ChevronUp, X, Loader2, Sparkles, AlertCircle, CheckCircle2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthContext } from "../../hooks/useAuthContext";

const OpportunitiesPreferences = () => {
  const { user, token, dispatch } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    major: user?.major || "",
    hobbies: user?.hobbies || [],
    learning_goals: user?.learning_goals || [],
    location: user?.location || ""
  });

  const [hobbyInput, setHobbyInput] = useState("");
  const [goalInput, setGoalInput] = useState("");

  // Sync state with user context if it changes externally
  useEffect(() => {
    if (!isEditing) {
      setFormData({
        major: user?.major || "",
        hobbies: user?.hobbies || [],
        learning_goals: user?.learning_goals || [],
        location: user?.location || ""
      });
    }
  }, [user, isEditing]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await fetch('/api/auth/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const json = await response.json();

      if (response.ok) {
        dispatch({ type: 'UPDATE_USER', payload: json.user });
        setSuccess(true);
        setTimeout(() => {
          setIsEditing(false);
          setSuccess(false);
        }, 800);
      } else {
        setError(json.error || "Failed to update preferences");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addItem = (field, value) => {
    const trimmed = value.trim();
    if (trimmed && !formData[field].includes(trimmed)) {
      setFormData({ ...formData, [field]: [...formData[field], trimmed] });
    }
  };

  const removeItem = (field, value) => {
    setFormData({ ...formData, [field]: formData[field].filter(v => v !== value) });
  };

  return (
    <div className="space-y-4">
      <div className={`stat-card !p-4 transition-all duration-300 ${isEditing ? 'border-secondary/20 shadow-lg ring-1 ring-secondary/5' : ''}`}>
        <AnimatePresence mode="wait">
          {!isEditing ? (
            <motion.div
              key="view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Header - View Mode - Minimal */}
              <div className={`flex items-center justify-between transition-all ${!isCollapsed ? 'mb-4' : 'mb-0'}`}>
                 <div className="flex items-center gap-2">
                   <Settings2 size={15} className="text-secondary" />
                   <h3 className="text-xs font-bold text-foreground">Preferences</h3>
                 </div>
                 <button 
                   onClick={() => setIsCollapsed(!isCollapsed)}
                   className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all border-none bg-transparent cursor-pointer"
                 >
                   {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                 </button>
              </div>

              <AnimatePresence>
                {!isCollapsed && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="text-[11px] text-muted-foreground font-medium leading-relaxed mb-5">
                      Personalise your career matches by editing these fields.
                    </p>

                    <div className="space-y-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-tight">Academic Major</span>
                        <span className="text-xs font-bold text-foreground">
                          {user?.major || "Not set"}
                        </span>
                      </div>
                      
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-tight">Interests & Hobbies</span>
                        <span className="text-xs font-bold text-foreground leading-relaxed">
                          {user?.hobbies?.length > 0 ? user.hobbies.join(", ") : "None added"}
                        </span>
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-tight">Learning Goals</span>
                        <span className="text-xs font-bold text-foreground leading-relaxed">
                          {user?.learning_goals?.length > 0 ? user.learning_goals.join(", ") : "None added"}
                        </span>
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-tight">Location Preference</span>
                        <span className="text-xs font-bold text-foreground">
                          {user?.location || "Not set"}
                        </span>
                      </div>
                    </div>

                    <button 
                      onClick={() => setIsEditing(true)}
                      className="w-full h-9 mt-6 rounded-lg bg-muted/40 hover:bg-muted text-foreground font-bold text-[11px] transition-all cursor-pointer border-none shadow-sm flex items-center justify-center gap-2 group"
                    >
                      <Sparkles size={12} className="text-secondary opacity-60 group-hover:opacity-100 transition-opacity" />
                      Customize Experience
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              key="edit"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              className="space-y-5"
            >
              {/* Header - Edit Mode - Minimal */}
              <div className="flex items-center justify-between mb-2">
                 <h3 className="text-xs font-bold text-foreground flex items-center gap-2">
                   <Sparkles size={14} className="text-secondary" /> Edit Preferences
                 </h3>
                 <button 
                   onClick={() => setIsEditing(false)}
                   className="p-1.5 rounded-md hover:bg-muted transition-all border-none bg-transparent text-muted-foreground hover:text-foreground cursor-pointer"
                 >
                   <X size={14} />
                 </button>
              </div>

              <div className="space-y-4">
                {/* Major */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-muted-foreground px-0.5">Academic Major</label>
                  <input
                    type="text"
                    value={formData.major}
                    onChange={(e) => setFormData({ ...formData, major: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg bg-muted/30 border border-border/50 focus:border-secondary/30 focus:bg-background text-xs font-bold text-foreground transition-all outline-none placeholder:text-muted-foreground/30"
                    placeholder="e.g. Computer Science"
                  />
                </div>

                {/* Hobbies */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-muted-foreground px-0.5">Interests & Hobbies</label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {formData.hobbies.map(hobby => (
                        <motion.span 
                          layout
                          key={hobby} 
                          className="px-2 py-0.5 rounded-md bg-secondary/10 text-secondary text-[10px] font-bold flex items-center gap-1.5 border border-secondary/15"
                        >
                          {hobby}
                          <button onClick={() => removeItem('hobbies', hobby)} className="hover:text-destructive transition-colors border-none bg-transparent p-0 cursor-pointer">
                            <X size={10} strokeWidth={3} />
                          </button>
                        </motion.span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={hobbyInput}
                      onChange={(e) => setHobbyInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addItem('hobbies', hobbyInput);
                          setHobbyInput("");
                        }
                      }}
                      className="w-full h-9 px-3 rounded-lg bg-muted/30 border border-border/50 focus:border-secondary/30 focus:bg-background text-xs font-bold text-foreground transition-all outline-none placeholder:text-muted-foreground/30"
                      placeholder="Add interest and press Enter..."
                    />
                  </div>
                </div>

                {/* Goals */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-muted-foreground px-0.5">Learning Goals</label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {formData.learning_goals.map(goal => (
                        <motion.span 
                          layout
                          key={goal} 
                          className="px-2 py-0.5 rounded-md bg-muted text-foreground text-[10px] font-bold flex items-center gap-1.5 border border-border/50"
                        >
                          {goal}
                          <button onClick={() => removeItem('learning_goals', goal)} className="hover:text-destructive transition-colors border-none bg-transparent p-0 cursor-pointer">
                            <X size={10} strokeWidth={3} />
                          </button>
                        </motion.span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addItem('learning_goals', goalInput);
                          setGoalInput("");
                        }
                      }}
                      className="w-full h-9 px-3 rounded-lg bg-muted/30 border border-border/50 focus:border-secondary/30 focus:bg-background text-xs font-bold text-foreground transition-all outline-none placeholder:text-muted-foreground/30"
                      placeholder="Add goal and press Enter..."
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-muted-foreground px-0.5">Location Preference</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg bg-muted/30 border border-border/50 focus:border-secondary/30 focus:bg-background text-xs font-bold text-foreground transition-all outline-none placeholder:text-muted-foreground/30"
                    placeholder="e.g. Remote"
                  />
                </div>

                {/* Status Feedback */}
                <AnimatePresence>
                  {(error || success) && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className={`text-[10px] font-bold py-1 px-1 rounded flex items-center gap-1.5 ${success ? "text-emerald-500" : "text-destructive"}`}
                    >
                      {success ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                      {success ? "Saved successfully" : error}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Save Button - Minimal */}
                <button
                  onClick={handleSave}
                  disabled={loading || success}
                  className={`
                    w-full h-10 mt-2 rounded-lg font-bold text-xs transition-all flex items-center justify-center gap-2
                    ${loading || success ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:opacity-95 active:scale-[0.98] cursor-pointer shadow-sm shadow-primary/20'}
                  `}
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : success ? (
                    <>
                      <CheckCircle2 size={15} /> Saved
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OpportunitiesPreferences;
